import { Dialog, Message } from '@alifd/next';
import { Box } from '@mui/material';
import { Form, Rate, Typography } from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '@/redux/store';
import { ResponseData as ContentData } from '@/types/api/questionnaire/getQuestionnaireContentFontDesk';
import { QuestionnaireAnswer } from '@/types/api/questionnaire/updateQuestionnaireAnswerFontDesk';

import { Data } from '../..';
import { Answers } from './type';

const { Item, useForm } = Form;

const QuestionnaireForm: React.FC<{
  questionnaire: {
    show: boolean;
    currentQuestionnaire: Data | null;
  };
  setQuestionnaire: React.Dispatch<
    React.SetStateAction<{
      show: boolean;
      currentQuestionnaire: Data | null;
    }>
  >;
  setTrigger: React.Dispatch<React.SetStateAction<Record<string, never>>>;
}> = ({ questionnaire, setQuestionnaire, setTrigger }) => {
  const [content, setContent] = useState<ContentData | null>(null);

  const user = useSelector((state: RootState) => state.user);

  const [form] = useForm();

  const getContent = useCallback(async () => {
    try {
      const res = await axios.get<ContentData>(
        '/ezcon/api/getQuestionnaireContentFontDesk',
        {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            userId: user.auth.user._id,
            searchTime: questionnaire.currentQuestionnaire?.searchTime,
            filler: questionnaire.currentQuestionnaire?.filler,
          },
        }
      );
      setContent(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [user.auth.user._id, questionnaire]);

  useEffect(() => {
    if (questionnaire.currentQuestionnaire && user.auth.user._id) {
      getContent();
    }
  }, [questionnaire, getContent, user.auth.user._id]);

  const handleClose = () => {
    setQuestionnaire({ show: false, currentQuestionnaire: null });
    setContent(null);
  };

  const handleFinish = async (val: Answers) => {
    const data: QuestionnaireAnswer[] = [];
    Object.entries(val).forEach(([questionnaireAnswerId, answer]) => {
      data.push({
        questionnaireAnswerId,
        answer,
      });
    });
    try {
      await axios.post('/ezcon/api/updateQuestionnaireAnswerFontDesk', data, {
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      setTrigger({});
      setQuestionnaire({ show: false, currentQuestionnaire: null });
      setContent(null);
      Message.success('問卷分數送出成功');
    } catch (err) {
      Message.error('問卷分數送出失敗');
      console.error(err);
    }
  };

  const handleOK = () => {
    if (content?.editable) {
      form.submit();
    } else {
      handleClose();
    }
  };

  return (
    <Dialog
      visible={questionnaire.show}
      title={
        <Typography.Title level={2}>{content?.title || ''}</Typography.Title>
      }
      onOk={handleOK}
      onCancel={handleClose}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: '600px',
          height: '600px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {content && (
          <>
            <Form
              {...{
                form,
                onFinish: handleFinish,
              }}
            >
              {content.service.map((s, sIndex) => (
                <Box key={sIndex}>
                  <Typography.Title level={3}>{s.name}</Typography.Title>
                  {s.vendors.map((v, vIndex) => (
                    <Box key={vIndex}>
                      <Typography.Title level={4}>{v.name}</Typography.Title>
                      {Object.entries(v.questions).map(
                        ([topic, question], qIndex) => (
                          <Box key={qIndex}>
                            <Typography.Title level={5}>
                              {question.description}
                            </Typography.Title>
                            <Box>
                              <Item
                                name={[v.questionnaireAnswerId, topic]}
                                initialValue={question.answer}
                              >
                                <Rate disabled={!content.editable} />
                              </Item>
                            </Box>
                          </Box>
                        )
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Form>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default QuestionnaireForm;
